import { defineStore } from "pinia";

import { IAttachment } from "../models/attachment"
import { IAttachmentApiResponse } from "../models/api/attachmentApiResponse"
import { IAttachmentFilter } from "../models/filters/attachmentFilter"
import AttachmentService from "../services/AttachmentService";
import { IAttachmentForm } from "../models/forms/attachmentForm";

import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";

import { IBoar } from "../models/boar";
import { IGroupMounting } from "../models/groupMounting";
import { useBoars } from "./boars.store";
import SelectedBoarViewModel from "../models/viewModels/SelectedBoarViewModel";
import { useGroupMountingStore } from "./groupMounting.store";
import { useMountingPlanTileDataStore } from "./computedStores/mountingPlanTileData.store";
import SchemaViewModel from "../models/viewModels/schemaViewModel";
import i18n from "@/i18n";

export const useAttachmentStore = defineStore("attachments", {
    state: () => ({
        datas: [] as Array<IAttachment>,
        current : {} as IAttachment,
        itemForm : {} as IAttachmentForm,
        isLoading: false as boolean,
        mountingPlanId: 0 as number,
        groupMountingId: 0 as number // le groupe de monte
    }),
    getters: {

        /**
         * Index Map des pièces jointes par groupMountingId — O(1) lookup.
         * Pinia recalcule ce getter uniquement quand state.datas change.
         */
        byGroupMountingId: (state): Map<number, IAttachment[]> => {
            const map = new Map<number, IAttachment[]>();
            for (const a of state.datas) {
                const id = a.groupMountingId ?? a.groupMounting?.id;
                if (!id) continue;
                let list = map.get(id);
                if (!list) { list = []; map.set(id, list); }
                list.push(a);
            }
            return map;
        },

        /**
         * Retourne les pièces jointes associées à un GroupMounting par son identifiant
         * Utilise byGroupMountingId (O(1)) au lieu d'un filter O(n)
         * @param groupMountingId Identifiant du GroupMounting
         * @returns Array<IAttachment>
         */
        getByGroupMountingId(): (groupMountingId: number) => Array<IAttachment> {
            return (groupMountingId: number): Array<IAttachment> => {
                return this.byGroupMountingId.get(groupMountingId) ?? [];
            };
        },
        /**
         * Retourne les pièces jointes associées à un Boar par son identifiant
         * @param boarId Identifiant du Boar
         * @returns Array<IAttachment>
         * */
        getByBoarId: (state) =>( boarId: number): Array<IGroupMounting> => {
            const result = state.datas.filter((attachment) => attachment.boarId === boarId)?.map((attachment) => { return attachment.groupMounting});
            if (!result)
                return [] as Array<IGroupMounting>;
            else
                return result as Array<IGroupMounting>;
        },
    },
    actions: {

        /**
         * Chargement des données dans le store
         * @param filter Le filtre à appliquer pour le chargement des données
         * @returns
         */
        async load(filter: IAttachmentFilter) {
            this.isLoading = true;
            let result = await AttachmentService.getAll<IAttachmentFilter, IAttachmentApiResponse>(filter);
            this.isLoading = false;
            this.datas = [];
            if (!result.error) {
                const boarStore = useBoars();
                const groupMountingStore = useGroupMountingStore();

                // Maps O(1) pour l'hydratation
                const boarMap = new Map(boarStore.datas.map(b => [b.id, b]));
                const gmMap = new Map(groupMountingStore.datas.map(g => [g.id, g]));

                const resultData = result.data as IAttachmentApiResponse[];
                const hydrated: IAttachment[] = [];
                for (const a of resultData) {
                    const model = { ...a } as unknown as IAttachment;
                    model.boar = boarMap.get(a.boarId);
                    model.groupMounting = gmMap.get(a.groupMountingId);
                    hydrated.push(model);
                }
                this.datas = hydrated;
            }
            return result;
        },

        /**
         * Récupération d'un rattachment par son identifiant
         * @param id
         * @returns
         */
        async get(id: number) {
            let result = await AttachmentService.getById<IAttachmentApiResponse>(id);
            if ( !result.error) {
                const hydrated = this._hydrateOne(result.data as IAttachmentApiResponse);
                let index = this.datas.findIndex((a) => a.id == id);
                if (index >= 0)
                    this.datas[index] = hydrated;
                this.current = hydrated;
                this.itemForm = this.toForm(hydrated);
            }
            return result;
        },

        /**
        * Récupération de l'objet dans le store
        * @param id Identifiant de l'objet
        * @returns
        */
        async searchStoreById (id: number) {
            this.current = this.datas.find((objet) => objet.id == id)!;
            this.itemForm = await this.toForm(this.current as IAttachment);
            return this.current;
        },

        /**
         * Mise à jour ou création d'un objet
         * @param maj
         * @returns
         */
        /**
         * Hydrate un attachment API response avec les objets boar et groupMounting
         */
        _hydrateOne(apiResponse: IAttachmentApiResponse): IAttachment {
            const boarStore = useBoars();
            const groupMountingStore = useGroupMountingStore();
            const model = { ...apiResponse } as unknown as IAttachment;
            model.boar = boarStore.datas.find(b => b.id === apiResponse.boarId);
            model.groupMounting = groupMountingStore.datas.find(g => g.id === apiResponse.groupMountingId);
            return model;
        },

        async save(maj:boolean = true, isPatch:boolean = false)
        {
            let result = {} as ServiceOperationResult<IAttachment | IDataOperationResult<IAttachment>>;
            if ( this.itemForm.id > 0) {
                result = await AttachmentService.update(this.itemForm);

                if ( !result.error)
                {
                    this.current = this._hydrateOne(result.data as IAttachmentApiResponse);
                    if ( maj ) {
                        let index = this.datas.findIndex((a) => a.id == this.itemForm.id);
                        if (index >= 0) {
                            this.datas[index] = this.current;
                        } else {
                            const error = new DataOperationError();
                            error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                            return new ServiceOperationResult<IDataOperationResult<IAttachment>>(true, {errors: error});
                        }
                    }
                }
            } else {
                result = await AttachmentService.create(this.itemForm);
                if ( !result.error)
                {
                    this.current = this._hydrateOne(result.data as IAttachmentApiResponse);
                    this.datas.push(this.current);
                }

            }
            return result;
        },

        /**
         * Delete de l'objet
         * @returns
         */
        async delete() {
            let result = await AttachmentService.delete<IAttachment>(this.current.id, true);

            if ( !result.error) {
                let index = this.datas.findIndex((a) => a.id == this.current.id);
                if (index >= 0) {
                    // On remove l'élément des données
                    this.datas.splice(index, 1);
                }

                this.current = this.newItem();
                this.itemForm = this.newForm();
            }
            return result;
        },

        async batchDelete(ids:number[]) {
            const result = await AttachmentService.batchDelete(ids,true);
            if (!result.error) {
                this.datas = this.datas.filter(item => !ids.includes(item.id));
            }
            return result;
        },

        /**
         * Transformation de l'objet en formulaire
         * @param item Batiment
         * @returns
         */
        toForm(item: IAttachment): IAttachmentForm {
            const form = {} as IAttachmentForm;

            form.id = item.id;

            // Clés étangéres
            form.boarId = item.boar ? item.boar.id : 0;
            form.groupMountingId = item.groupMounting ? item.groupMounting.id : 0

            this.itemForm = form;
            return form;
        },

        /**
         * Permet d'initialiser un objet vide pour le formulaire
         * @returns
         */
        newForm(): IAttachmentForm {
            this.itemForm = {
                id: 0,
                boarId: 0,
                groupMountingId: 0,
            } as IAttachmentForm;
            return this.itemForm;
        },

        /**
         *  Permet d'initialiser un objet vide pour les referentiels
         * @returns
         */
        newItem(): IAttachment {

            this.current = {
                id: 0,
                boar: { id: 0 } as IBoar,
                boarId: 0,
                groupMountingId: 0,
                groupMounting: { id: 0 } as IGroupMounting,
            } as IAttachment;

            this.itemForm = this.toForm(this.current);
            return this.current;
        },

        /**
         * Retourne la liste des animaux par Plan de monte en recherchant l'animal dans le store Boars
         */
        getByMountingPlanId(selectedSchemas: SchemaViewModel[]): Array<SelectedBoarViewModel> {
            const tileDataStore = useMountingPlanTileDataStore();

            const boarStore = useBoars();
            const groupStore = useGroupMountingStore();

            // Use mountingPlanId instead of mountingPlan.id to avoid undefined access
            const planGroups = groupStore.datas.filter(g => g.mountingPlanId == this.mountingPlanId);
            if(planGroups.length == 0) {
                return [];
            }

            // Create Set of planGroupIds for O(1) membership checks
            const planGroupIds = new Set(planGroups.map(g => g.id));

            // Filter attachments by groupMountingId using Set for O(1) checks
            const planAttachments = this.datas.filter(a => a.groupMountingId && planGroupIds.has(a.groupMountingId));

            // Cache boars by id to O(1)
            const boarById = new Map<number, IBoar>(
                boarStore.datas.map(b => [b.id, b] as const)
            );

            // Cache groupMountings by id for O(1) lookups
            const groupById = new Map(groupStore.datas.map(g => [g.id, g]));

            // Keyed aggregation: schemaId:groupMountingId
            const agg = new Map<string, SelectedBoarViewModel>();

            for (const pa of planAttachments) {
                if(!pa.boarId) continue;
                const boar = boarById.get(pa.boarId);
                if (!boar) continue;

                const schemaId = boar.geneticType?.id ?? 0;
                const groupId  = pa.groupMountingId ?? 0;
                const key      = `${schemaId}:${groupId}`;
                const isCommercial = ['1', '2', '3'].includes(boar.conservationAptitude?.code3 ?? '') && boar.productionStatus?.code3 == 'P';

                const tileData = tileDataStore.tileDataByBoarId.get(boar.id);
                const doses = Number(tileData?.spzAverage ?? "0");
                const dosesCommercial = isCommercial ? doses : 0;

                const displaySchema = selectedSchemas.length == 0 || (selectedSchemas.length > 0 && selectedSchemas.some(s => s.id == schemaId))

                let vm = agg.get(key);
                if(displaySchema) {
                    if (!vm) {
                        // Get groupMounting from Map for O(1) lookup
                        const groupMounting = groupById.get(groupId);
                        vm = {
                            schemaId,
                            schemaName: boar.geneticType?.code3 ?? "",
                            groupName: `G${groupMounting?.number ?? ""}`,
                            groupMountingId: groupId,
                            nbrBoars: 0,
                            nbrArmadas: 0,
                            nbrDoses: 0,
                            nbrDosesCommercial: 0,
                            nbrCommercialBoars: 0
                        };
                        agg.set(key, vm);
                    }

                    vm.nbrBoars += 1;
                    if (boar.armada) vm.nbrArmadas += 1;
                    vm.nbrDoses += doses;
                    vm.nbrDosesCommercial += dosesCommercial;
                    if (isCommercial) vm.nbrCommercialBoars += 1;
                }
            }

            return [...agg.values()];
        }
    }
})
